import React, { useEffect, useRef } from 'react';

// Canvas-based Matrix "digital rain" effect.
// Replaces the previous <video> implementation because Safari (and sometimes
// iOS Chrome) refuse to honour muted-autoplay and render a native play-button
// overlay on blocked <video> elements. Canvas has no such policy.

const TOP_MAX_OPACITY = 0.6;
const BOTTOM_MAX_OPACITY = 0.5;
const TOP_FADE_END = 0.35;
const BOTTOM_FADE_START = 0.65;

const FONT_SIZE = 16;
const RAIN_COLOR = '#9fe870';           // lime-ish green, matches brand accent
const TRAIL_FADE = 'rgba(10, 15, 10, 0.08)';
const GLYPHS = 'アイウエオカキクケコサシスセソタチツテトナニヌネノ0123456789<>#$%&?/';

const clamp01 = (x) => Math.max(0, Math.min(1, x));

const useMatrixRain = (canvasRef, flipped = false) => {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let drops = [];
    let columns = 0;
    let rafId = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      columns = Math.ceil(window.innerWidth / FONT_SIZE);
      drops = new Array(columns).fill(0).map(() => Math.random() * -50);
      ctx.fillStyle = '#0a0f0a';
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);
    };

    const draw = () => {
      ctx.fillStyle = TRAIL_FADE;
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);

      ctx.fillStyle = RAIN_COLOR;
      ctx.font = `${FONT_SIZE}px monospace`;

      for (let i = 0; i < columns; i++) {
        const ch = GLYPHS.charAt(Math.floor(Math.random() * GLYPHS.length));
        const x = i * FONT_SIZE;
        const y = drops[i] * FONT_SIZE;
        ctx.fillText(ch, x, y);
        if (y > window.innerHeight && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i] += 1;
      }

      rafId = window.requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener('resize', resize);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
    };
    // flipped is only used in style, not in effect logic
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasRef]);
};

const useScrollFade = (topLayerRef, bottomLayerRef) => {
  useEffect(() => {
    const topLayer = topLayerRef.current;
    const bottomLayer = bottomLayerRef.current;
    if (!topLayer || !bottomLayer) return;

    let ticking = false;
    const updateOpacities = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const maxScroll = (doc.scrollHeight - window.innerHeight) || 1;
      const p = clamp01(scrollTop / maxScroll);
      const topT = clamp01(1 - (p / TOP_FADE_END));
      const bottomT = clamp01((p - BOTTOM_FADE_START) / (1 - BOTTOM_FADE_START));
      topLayer.style.opacity = String(topT * TOP_MAX_OPACITY);
      bottomLayer.style.opacity = String(bottomT * BOTTOM_MAX_OPACITY);
      ticking = false;
    };
    const requestTick = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateOpacities);
        ticking = true;
      }
    };
    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick);
    requestTick();
    return () => {
      window.removeEventListener('scroll', requestTick);
      window.removeEventListener('resize', requestTick);
    };
  }, [topLayerRef, bottomLayerRef]);
};

export const VideoBackground = () => {
  const topLayerRef = useRef(null);
  const bottomLayerRef = useRef(null);
  const topCanvasRef = useRef(null);
  const bottomCanvasRef = useRef(null);

  useMatrixRain(topCanvasRef);
  useMatrixRain(bottomCanvasRef, true);
  useScrollFade(topLayerRef, bottomLayerRef);

  const canvasStyle = { display: 'block', width: '100%', height: '100%' };

  return (
    <>
      <div className="fixed inset-0 bg-[var(--bg-page)]" style={{ zIndex: -10000 }} />

      <div id="matrixTop" ref={topLayerRef} aria-hidden="true" data-testid="matrix-top-layer">
        <canvas ref={topCanvasRef} style={canvasStyle} />
      </div>

      <div id="matrixBottom" ref={bottomLayerRef} aria-hidden="true" data-testid="matrix-bottom-layer">
        <canvas ref={bottomCanvasRef} style={{ ...canvasStyle, transform: 'scaleY(-1)' }} />
      </div>
    </>
  );
};
